import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  admin,
  type AdminClub,
  type AdminClubStaff,
  type AdminUserRow,
  type AdminQuestionnaireSummary,
} from '../../api/admin'
import { club } from '../../api/club'
import { useAuth } from '../../auth/AuthContext'
import { ApiError } from '../../api/client'
import { CreateUserModal } from './CreateUserModal'
import { InviteModal } from './InviteModal'
import {
  DEFAULT_DIR,
  buildClubIndex,
  filterUsers,
  sortUsers,
  type AccessFilter,
  type ClubFilter,
  type ClubIndex,
  type RoleFilter,
  type SortDir,
  type SortKey,
  type StatusFilter,
  type UsersFilter,
} from './adminUsersView'

/** Tope del backend (`limit` le=500). Filtros y orden corren en el cliente sobre esta página. */
const USERS_LIMIT = 500

const SORT_KEYS: SortKey[] = ['username', 'display', 'club', 'sessions', 'trials', 'last', 'joined']

function readFilter(params: URLSearchParams): UsersFilter {
  const club = params.get('club')
  return {
    q: params.get('search') ?? '',
    club: club === 'none' ? 'none' : club && /^\d+$/.test(club) ? Number(club) : 'all',
    role: (params.get('rol') as RoleFilter | null) ?? 'all',
    access: (params.get('acceso') as AccessFilter | null) ?? 'all',
    status: (params.get('estado') as StatusFilter | null) ?? 'all',
  }
}

/**
 * /admin/users — searchable list with header actions (+ Crear usuario, Clubes),
 * and row-level Acciones column (toggle admin, invite, results, detail).
 * Superadmins show the badge but admin toggle isn't available.
 */
/**
 * /admin/users — lista con columna Club, filtros (texto, club, rol, acceso,
 * estado) y orden por columna, todo en el cliente y persistido en la query
 * string. Header actions (+ Crear usuario) y columna Acciones por fila.
 */
export function AdminUsers() {
  const { user: currentUser, refresh } = useAuth()
  const [params, setParams] = useSearchParams()
  const filter = readFilter(params)
  const sortParam = params.get('sort') as SortKey | null
  const sortKey: SortKey = sortParam && SORT_KEYS.includes(sortParam) ? sortParam : 'joined'
  const sortDir: SortDir = (params.get('dir') as SortDir | null) ?? DEFAULT_DIR[sortKey]
  const [rows, setRows] = useState<AdminUserRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [clubs, setClubs] = useState<AdminClub[]>([])
  const [clubIndex, setClubIndex] = useState<ClubIndex | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState<string | null>(null)
  const [questionnaires, setQuestionnaires] = useState<AdminQuestionnaireSummary[]>([])
  const [impersonating, setImpersonating] = useState<number | null>(null)

  const load = async () => {
    setError(null)
    try {
      const r = await admin.users(undefined, USERS_LIMIT)
      setRows(r.users)
      setTotal(r.total)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }

  const loadClubs = async () => {
    try {
      const { clubs: list } = await admin.clubs()
      setClubs(list)
      const details = await Promise.all(list.map((c) => admin.club(c.id)))
      const staff = await Promise.all(
        list.map((c) => admin.clubStaff(c.id).then((r) => [c.id, r.staff] as const)),
      )
      setClubIndex(buildClubIndex(details, Object.fromEntries(staff) as Record<number, AdminClubStaff[]>))
    } catch (e) {
      setClubIndex(new Map())
      setError(e instanceof ApiError ? `No se pudieron cargar los clubes: ${e.message}` : 'Error de red al cargar clubes.')
    }
  }

  useEffect(() => {
    void load()
    void loadClubs()
  }, [])

  const visible = useMemo(() => {
    if (!rows) return null
    const index = clubIndex ?? new Map()
    return sortUsers(filterUsers(rows, index, filter), index, sortKey, sortDir)
    // `filter` es derivado de `params`; dependemos de la query string entera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, clubIndex, params])

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params)
    if (value === null || value === '' || value === 'all') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  function toggleSort(key: SortKey) {
    const next = new URLSearchParams(params)
    const dir: SortDir =
      key === sortKey ? (sortDir === 'asc' ? 'desc' : 'asc') : DEFAULT_DIR[key]
    next.set('sort', key)
    next.set('dir', dir)
    setParams(next, { replace: true })
  }

  const filtered =
    filter.q !== '' ||
    filter.club !== 'all' ||
    filter.role !== 'all' ||
    filter.access !== 'all' ||
    filter.status !== 'all'

  function SortTh({ k, label, right }: { k: SortKey; label: string; right?: boolean }) {
    const active = k === sortKey
    return (
      <th className={right ? 'text-right' : undefined} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-xs ${active ? 'text-text-primary' : ''}`}
        >
          {label}
          <span className={active ? '' : 'opacity-30'} aria-hidden>
            {active && sortDir === 'asc' ? '\u25B2' : '\u25BC'}
          </span>
        </button>
      </th>
    )
  }

  async function toggleDemoAccess(row: AdminUserRow) {
    const has = row.roles.includes('tester')
    try {
      await admin.toggleAccess(row.id, 'tester', has ? 'revoke' : 'grant')
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }

  async function handleImpersonate(userId: number) {
    setImpersonating(userId)
    try {
      await admin.impersonate(userId)
      await refresh()
      const clubData = await club.me()
      if (clubData.clubs.length > 0) {
        window.location.href = '/club'
      } else {
        window.location.href = '/'
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al impersonar usuario')
      setImpersonating(null)
    }
  }

  return (
    <section>
      <div className="page-header">
        <h1>Usuarios</h1>
        <div className="flex items-center gap-sm">
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Crear usuario
          </button>
        </div>
      </div>
      <p className="text-sm text-text-muted mb-md">
        {visible && filtered ? `${visible.length} de ` : ''}
        {total} usuario{total === 1 ? '' : 's'}
        {rows && total > rows.length && (
          <span className="text-warning">
            {' '}
            (se muestran los {rows.length} más recientes; filtros y orden aplican sobre esos)
          </span>
        )}
      </p>

      {error && <div className="banner-err mb-md">{error}</div>}

      <div className="filter-bar">
        <input
          type="search"
          placeholder="Buscar usuario, email, nombre o club"
          value={filter.q}
          onChange={(e) => setParam('search', e.target.value)}
          className="input w-72"
          aria-label="Buscar"
        />
        <label className="flex items-center gap-xs">
          Club
          <select
            className="input"
            value={String(filter.club)}
            onChange={(e) => setParam('club', e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="none">Sin club</option>
            {[...clubs]
              .sort((a, b) => a.name.localeCompare(b.name, 'es'))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="flex items-center gap-xs">
          Rol
          <select
            className="input"
            value={filter.role}
            onChange={(e) => setParam('rol', e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="admin">Administrador</option>
            <option value="staff">Staff de club</option>
            <option value="jugador">Jugador</option>
            <option value="tester">Acceso de prueba</option>
            <option value="device_admin">Dispositivos</option>
            <option value="sin_rol">Sin rol</option>
          </select>
        </label>
        <label className="flex items-center gap-xs">
          Acceso
          <select
            className="input"
            value={filter.access}
            onChange={(e) => setParam('acceso', e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="demo">Con demo</option>
            <option value="sin_demo">Sin demo</option>
          </select>
        </label>
        <label className="flex items-center gap-xs">
          Estado
          <select
            className="input"
            value={filter.status}
            onChange={(e) => setParam('estado', e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </label>
        {filtered && (
          <Link to="/admin/users" className="text-sm text-text-muted">
            limpiar
          </Link>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <SortTh k="username" label="Username" />
              <SortTh k="display" label="Display" />
              <SortTh k="club" label="Club" />
              <th>Rol</th>
              <th>Acceso</th>
              <SortTh k="sessions" label="Sesiones" right />
              <SortTh k="trials" label="Trials" right />
              <SortTh k="last" label="Última" />
              <SortTh k="joined" label="Alta" />
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visible === null && (
              <tr>
                <td colSpan={10} className="text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {visible?.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-text-muted">
                  Sin resultados.
                </td>
              </tr>
            )}
            {visible?.map((u) => {
              const isTester = u.roles.includes('tester')
              const links = clubIndex?.get(u.id) ?? []
              return (
                <tr key={u.id}>
                  <td>
                    <Link to={`/admin/users/${u.id}`} className="underline-offset-2 hover:underline">
                      {u.username}
                    </Link>{' '}
                    {u.is_superuser && <span className="tag-ok ml-1">Administrador</span>}
                    {!u.is_active && <span className="tag-err ml-1">inactivo</span>}
                  </td>
                  <td>{u.display_name || '—'}</td>
                  <td>
                    {clubIndex === null ? (
                      <span className="text-text-muted">…</span>
                    ) : links.length === 0 ? (
                      '—'
                    ) : (
                      <span className="flex flex-wrap items-center gap-xs">
                        {links.map((l) => (
                          <Link
                            key={`${l.club_id}-${l.kind}`}
                            to={`/admin/clubs/${l.club_id}`}
                            className="underline-offset-2 hover:underline"
                          >
                            {l.club_name}
                            {l.kind === 'staff' && <span className="tag ml-1">staff</span>}
                          </Link>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>
                    {u.is_superuser ? (
                      <span className="tag-ok">Administrador</span>
                    ) : (
                      <span className="flex items-center gap-sm">
                        {u.roles.includes('device_admin') && <span className="tag">Dispositivos</span>}
                        {isTester && <span className="tag">Acceso de prueba</span>}
                        {u.roles.includes('jugador') && <span className="tag">Jugador</span>}
                      </span>
                    )}
                  </td>
                  <td>
                    {u.has_demo_access ? (
                      <span className="tag-ok">demo</span>
                    ) : (
                      <span className="tag">—</span>
                    )}
                  </td>
                  <td className="text-right">{u.n_sessions}</td>
                  <td className="text-right">{u.n_trials}</td>
                  <td>
                    {u.last_session_at
                      ? new Date(u.last_session_at).toLocaleDateString('es-AR')
                      : '—'}
                  </td>
                  <td>
                    {u.date_joined
                      ? new Date(u.date_joined).toLocaleDateString('es-AR')
                      : '—'}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-xs">
                      {!u.is_superuser && (
                        <button
                          type="button"
                          className="btn-ghost px-sm py-xs text-xs"
                          onClick={() => void toggleDemoAccess(u)}
                        >
                          {isTester ? 'Quitar acceso de prueba' : 'Dar acceso de prueba'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-ghost px-sm py-xs text-xs"
                        onClick={() => setInviteEmail(u.email || '')}
                      >
                        Invitar
                      </button>
                      <Link
                        to={`/admin/athletes/${u.id}`}
                        className="btn-ghost px-sm py-xs text-xs no-underline"
                      >
                        Resultados
                      </Link>
                      <Link
                        to={`/admin/users/${u.id}`}
                        className="btn-ghost px-sm py-xs text-xs no-underline"
                      >
                        Detalle
                      </Link>
                      {!u.is_superuser && currentUser?.user_id !== u.id && (
                        <button
                          type="button"
                          className="btn-ghost px-sm py-xs text-xs"
                          onClick={() => void handleImpersonate(u.id)}
                          disabled={impersonating === u.id}
                        >
                          {impersonating === u.id ? 'Entrando…' : 'Entrar como'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onUserCreated={() => {
            setShowCreateModal(false)
            void load()
          }}
        />
      )}

      {inviteEmail && questionnaires.length > 0 && (
        <InviteModal
          defaultEmail={inviteEmail}
          defaultCode={questionnaires[0]?.code || ''}
          codes={questionnaires.map((q) => ({ code: q.code, name_es: q.name_es }))}
          onClose={() => setInviteEmail(null)}
        />
      )}
    </section>
  )
}
